export type PublicLocale = 'en' | 'nl';

export const DEFAULT_PUBLIC_LOCALE: PublicLocale = 'en';
export const PUBLIC_LOCALES: PublicLocale[] = ['en', 'nl'];

export function isPublicLocale(value: string): value is PublicLocale {
  return PUBLIC_LOCALES.includes(value as PublicLocale);
}

export function getLocaleFromPathname(pathname: string | null | undefined): PublicLocale {
  if (!pathname) {
    return DEFAULT_PUBLIC_LOCALE;
  }

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (firstSegment && isPublicLocale(firstSegment)) {
    return firstSegment;
  }

  return DEFAULT_PUBLIC_LOCALE;
}

export function hasLocalePrefix(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }

  const firstSegment = pathname.split('/').filter(Boolean)[0];
  return Boolean(firstSegment && isPublicLocale(firstSegment));
}

export function stripLocalePrefix(pathname: string | null | undefined): string {
  if (!pathname) {
    return '/';
  }

  const hasQuery = pathname.includes('?');
  const hasHash = pathname.includes('#');
  const splitIndex = hasQuery
    ? pathname.indexOf('?')
    : hasHash
      ? pathname.indexOf('#')
      : pathname.length;

  const cleanPath = pathname.slice(0, splitIndex);
  const suffix = pathname.slice(splitIndex);
  const segments = cleanPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return `/${suffix}`;
  }

  if (isPublicLocale(segments[0])) {
    const rest = segments.slice(1).join('/');
    return `${rest ? `/${rest}` : '/'}${suffix}`;
  }

  return `${cleanPath || '/'}${suffix}`;
}

export function localizeHref(locale: PublicLocale, href: string): string {
  if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return href;
  }

  if (href.startsWith('#')) {
    return `/${locale}${href}`;
  }

  const [pathPart, hashPart] = href.split('#');
  const normalizedPath = stripLocalePrefix(pathPart || '/');
  const pathWithPrefix = normalizedPath === '/' ? `/${locale}` : `/${locale}${normalizedPath}`;
  return hashPart ? `${pathWithPrefix}#${hashPart}` : pathWithPrefix;
}

export function switchLocalePathname(
  pathname: string | null | undefined,
  targetLocale: PublicLocale
): string {
  const normalized = stripLocalePrefix(pathname || '/');
  return normalized === '/' ? `/${targetLocale}` : `/${targetLocale}${normalized}`;
}

export function isBookingPath(pathname: string | null | undefined): boolean {
  const normalized = stripLocalePrefix(pathname || '/');
  return (
    normalized.startsWith('/book') ||
    normalized.startsWith('/auth') ||
    normalized.startsWith('/account') ||
    normalized.startsWith('/admin')
  );
}
